import './RigCalibration.css';
import { RetortHistory } from '@/models/models';
import {
  fetchRetortHistory,
  addRetortHistory as addRetortHistoryApi,
  deleteRetortHistory,
} from '@/api/calibrationApi';
import { RetortHistoryProps } from './types';
import { TableSkeletonRows } from '@/components/LoadingSkeleton/LoadingSkeleton';
import { LocalizedNumberInput } from '@/components/LocalizedNumberInput/LocalizedNumberInput';
import {
  clampPercentage,
  getApiErrorMessage,
  isPercentageValid,
  isUnauthorizedError,
} from './utils';
import { CALIBRATION_SLOTS } from './constants';
import { useSampleSourceCatalog } from '@/hooks/useSampleSourceCatalog';
import { calibrationSlotDisplayName } from '@/utils/sampleSources';
import { formatFixedNumber } from '@/utils/numberFormat';

import {
  Card,
  Text,
  Title,
  Alert,
  Group,
  Stack,
  Divider,
  Table,
  Button,
  Checkbox,
  Select,
} from '@mantine/core';

import IconAlertCircle from '@tabler/icons-react/IconAlertCircle';
import IconTrash from '@tabler/icons-react/IconTrash';
import IconPlus from '@tabler/icons-react/IconPlus';

import { formatDisplayDateTime } from '@/utils/dateFormat';
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/i18n/LanguageContext';

export default function RetortHistoryComponent({
  wellId,
  selectedRetortHistory,
  onRetortHistorySelect,
  density,
  oilPercent,
  waterPercent,
  onDensityChange,
  onOilPercentChange,
  onWaterPercentChange,
  onValuesChange,
}: RetortHistoryProps) {
  const { t, language } = useLanguage();
  const { sampleSources } = useSampleSourceCatalog();
  const navigate = useNavigate();
  const [retortHistory, setRetortHistory] = useState<RetortHistory[]>([]);
  const [retortHistoryLoading, setRetortHistoryLoading] = useState(false);
  const [retortHistoryError, setRetortHistoryError] = useState<string | null>(null);
  const [deletingRetortHistoryId, setDeletingRetortHistoryId] = useState<number | null>(null);
  const [addingRetortHistory, setAddingRetortHistory] = useState(false);
  const [oilPercentError, setOilPercentError] = useState<string | null>(null);
  const [waterPercentError, setWaterPercentError] = useState<string | null>(null);
  const [sampleSource, setSampleSource] = useState<string | null>(null);

  // Fetch retort history
  useEffect(() => {
    const loadRetortHistory = async () => {
      if (!wellId || isNaN(Number(wellId))) {
        setRetortHistory([]);
        return;
      }

      try {
        setRetortHistoryLoading(true);
        setRetortHistoryError(null);

        const sortedHistory = await fetchRetortHistory(Number(wellId));
        setRetortHistory(sortedHistory);
      } catch (error: unknown) {
        console.error('Error fetching retort history:', error);
        setRetortHistoryError(t('calibration.loadRetortError'));
        setRetortHistory([]);
      } finally {
        setRetortHistoryLoading(false);
      }
    };

    loadRetortHistory();
  }, [wellId, t]);

  const handleRetortHistorySelect = (history: RetortHistory | null) => {
    onRetortHistorySelect(history);
  };

  const handleAddRetortHistory = useCallback(async () => {
    if (!wellId || isNaN(Number(wellId))) {
      return;
    }

    // Validate that all fields are filled
    if (density === '' || oilPercent === '' || waterPercent === '' || !sampleSource) {
      setRetortHistoryError(t('calibration.fillAllFields'));
      setTimeout(() => setRetortHistoryError(null), 3000);
      return;
    }

    try {
      setAddingRetortHistory(true);
      setRetortHistoryError(null);

      const sortedHistory = await addRetortHistoryApi(
        Number(wellId),
        typeof density === 'number' ? density : 0,
        typeof oilPercent === 'number' ? oilPercent : 0,
        typeof waterPercent === 'number' ? waterPercent : 0,
        sampleSource
      );

      setRetortHistory(sortedHistory);

      // Clear fields after successful save
      onDensityChange('');
      onOilPercentChange('');
      onWaterPercentChange('');
      setSampleSource(null);
      if (onValuesChange) {
        onValuesChange('', '', '');
      }
    } catch (error: unknown) {
      console.error('Error adding retort history:', error);
      if (isUnauthorizedError(error)) {
        setRetortHistoryError(t('calibration.unauthorized'));
        setTimeout(() => {
          navigate('/authorization');
        }, 2000);
      } else {
        setRetortHistoryError(
          getApiErrorMessage(error, t('calibration.addRetortError'))
        );
        setTimeout(() => setRetortHistoryError(null), 3000);
      }
    } finally {
      setAddingRetortHistory(false);
    }
  }, [wellId, density, oilPercent, waterPercent, sampleSource, navigate, onDensityChange, onOilPercentChange, onWaterPercentChange, onValuesChange, t]);

  const handleDeleteRetortHistory = useCallback(async (historyId: number) => {
    try {
      setDeletingRetortHistoryId(historyId);
      await deleteRetortHistory(historyId);

      // Remove from local state
      setRetortHistory((prev) => prev.filter((item) => item.id !== historyId));
      
      // Clear selection if deleted item was selected
      if (selectedRetortHistory?.id === historyId) {
        onRetortHistorySelect(null);
      }
    } catch (error: unknown) {
      console.error('Error deleting retort history:', error);
      setRetortHistoryError(t('calibration.deleteRetortError'));
      setTimeout(() => setRetortHistoryError(null), 3000);
    } finally {
      setDeletingRetortHistoryId(null);
    }
  }, [selectedRetortHistory, onRetortHistorySelect, t]);

    return (
            <Card withBorder padding="md" radius="md" className="rc-card">
                <Title order={2} mb="xs" className="rc-sectionTitle rc-sectionTitle--light">
                  {t('calibration.retortStep')}
                </Title>

                <Divider my="md" className="rc-divider" />

                <Stack gap="md">
                  <Group gap="md" align="flex-end" justify="center" className="rc-wizardInputsRow">
                    <Stack gap="xs" className="rc-wizardInputStack">
                      <LocalizedNumberInput
                        label={t('calibration.mudDensity')}
                        value={density}
              onChange={(value) => onDensityChange(typeof value === 'number' ? value : '')}
                        min={0}
                        step={1}
                        decimalScale={2}
                        size="sm"
                        required
                        classNames={{ root: 'rc-wizardNumberInput' }}
                      />
                    </Stack>
                    <Stack gap="xs" className="rc-wizardInputStack">
                      <LocalizedNumberInput
                        label={t('calibration.oilPercent')}
                        value={oilPercent}
                        onChange={(value) => {
                          if (typeof value === 'number') {
                            // Validate value
                  if (!isPercentageValid(value)) {
                              setOilPercentError(t('calibration.rangePercent'));
                              // Clamp value between 0.0 and 100.0
                    const clampedValue = clampPercentage(value);
                    onOilPercentChange(clampedValue);
                            } else {
                              setOilPercentError(null);
                    onOilPercentChange(value);
                            }
                          } else {
                            setOilPercentError(null);
                  onOilPercentChange('');
                          }
                        }}
                        onBlur={() => {
                          // Clear error on blur if value is valid
                if (typeof oilPercent === 'number' && isPercentageValid(oilPercent)) {
                            setOilPercentError(null);
                          }
                        }}
                        min={0}
                        max={100}
                        step={1}
                        decimalScale={2}
                        size="sm"
                        required
              error={!!oilPercentError}
                        classNames={{ root: 'rc-wizardNumberInput' }}
                      />
                    </Stack>
                    <Stack gap="xs" className="rc-wizardInputStack">
                      <LocalizedNumberInput
                        label={t('calibration.waterPercent')}
                        value={waterPercent}
                        onChange={(value) => {
                          if (typeof value === 'number') {
                            // Validate value
                  if (!isPercentageValid(value)) {
                              setWaterPercentError(t('calibration.rangePercent'));
                              // Clamp value between 0.0 and 100.0
                    const clampedValue = clampPercentage(value);
                    onWaterPercentChange(clampedValue);
                            } else {
                              setWaterPercentError(null);
                    onWaterPercentChange(value);
                            }
                          } else {
                            setWaterPercentError(null);
                  onWaterPercentChange('');
                          }
                        }}
                        onBlur={() => {
                          // Clear error on blur if value is valid
                if (typeof waterPercent === 'number' && isPercentageValid(waterPercent)) {
                            setWaterPercentError(null);
                          }
                        }}
                        min={0}
                        max={100}
                        step={1}
                        decimalScale={2}
                        size="sm"
                        required
              error={!!waterPercentError}
                        classNames={{ root: 'rc-wizardNumberInput' }}
                      />
                    </Stack>
                    <Stack gap="xs" className="rc-wizardInputStack">
                      <Select
                        label={t('results.sampleSource')}
                        placeholder={t('calibration.selectSampleSource')}
                        value={sampleSource}
                        onChange={setSampleSource}
                        data={CALIBRATION_SLOTS.map((source) => ({
                          value: source.key,
                          label: calibrationSlotDisplayName(
                            sampleSources,
                            source.key,
                            language
                          ),
                        }))}
                        size="sm"
                        required
                        searchable
                        comboboxProps={{
                          withinPortal: false,
                          dropdownPadding: 8,
                        }}
                        styles={{
                          dropdown: {
                            maxHeight: '400px !important'
                          }
                        }}
                        classNames={{ root: 'rc-wizardNumberInput' }}
                      />
                    </Stack>
                  </Group>
                  <Group justify="center" mt="md">
                    <Button
                      leftSection={<IconPlus size={16} />}
                      onClick={handleAddRetortHistory}
                      loading={addingRetortHistory}
                      disabled={addingRetortHistory || density === '' || oilPercent === '' || waterPercent === '' || !sampleSource}
                      size="sm"
                      className="rc-button"
                    >
                      {t('calibration.saveRetort')}
                    </Button>
                  </Group>
                </Stack>

                <Divider my="md" className="rc-divider" />

          {retortHistoryError && (
            <Alert icon={<IconAlertCircle size="1rem" />} title={t('common.error')} color="red" mb="md" withCloseButton onClose={() => setRetortHistoryError(null)} className="rc-alert">
              {retortHistoryError}
            </Alert>
          )}

      <div className="rc-tableContainer">
                <Table
                  striped
                  highlightOnHover
                  withTableBorder
                  className="rc-table rc-table--compact"
                  aria-busy={retortHistoryLoading}
                >
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th className="rc-checkboxCell"></Table.Th>
                      <Table.Th><Text fw={700}>{t('calibration.date')}</Text></Table.Th>
                      <Table.Th><Text fw={700}>{t('calibration.density')}</Text></Table.Th>
                      <Table.Th><Text fw={700}>{t('results.sampleSource')}</Text></Table.Th>
                      <Table.Th><Text fw={700}>{t('calibration.oilPercent')}</Text></Table.Th>
                      <Table.Th><Text fw={700}>{t('calibration.waterPercent')}</Text></Table.Th>
                      <Table.Th><Text fw={700}>{t('common.actions')}</Text></Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                {retortHistoryLoading ? (
                      <TableSkeletonRows columns={7} />
                ) : retortHistory.length === 0 ? (
                      <Table.Tr>
                <Table.Td colSpan={7} className="rc-emptyStateCell">
                      <Text c="dimmed">{t('calibration.noRetortHistory')}</Text>
                        </Table.Td>
                      </Table.Tr>
                    ) : (
                  retortHistory.map((item) => {
                    const isSelected = selectedRetortHistory?.id === item.id;
                        return (
                          <Table.Tr
                            key={item.id}
                        onClick={() => handleRetortHistorySelect(isSelected ? null : item)}
                        className={`rc-retortRow ${isSelected ? 'rc-retortRow--selected' : ''}`}
                      >
                        <Table.Td className="rc-checkboxCell">
                          <Checkbox
                            checked={isSelected}
                            onChange={() => handleRetortHistorySelect(isSelected ? null : item)}
                            onClick={(e) => e.stopPropagation()}
                            classNames={{ input: 'rc-checkbox' }}
                          />
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{formatDisplayDateTime(item.date, 'N/A')}</Text>
                        </Table.Td>
                            <Table.Td>
                            <Text size="sm" fw={500}>
                              {formatFixedNumber(item.mud_density, language, 2)}
                                  </Text>
                    </Table.Td>
                            <Table.Td>
                              <Text size="sm">
                                {item.sample_source
                                  ? calibrationSlotDisplayName(
                                    sampleSources,
                                    item.sample_source,
                                    language
                                  )
                                  : '—'}
                              </Text>
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm" fw={500}>
                            {formatFixedNumber(item.oil_percentage, language, 2)}
                              </Text>
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm" fw={500}>
                            {formatFixedNumber(item.water_percentage, language, 2)}
                              </Text>
                            </Table.Td>
                            <Table.Td>
                          <Button
                            variant="subtle"
                            color="red"
                            size="xs"
                            leftSection={<IconTrash size={14} />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteRetortHistory(item.id);
                            }}
                            loading={deletingRetortHistoryId === item.id}
                            disabled={deletingRetortHistoryId !== null}
                          >
                            {t('calibration.delete')}
                          </Button>
                            </Table.Td>
                          </Table.Tr>
                        );
                      })
                    )}
                  </Table.Tbody>
                </Table>
              </div>
            </Card>
  );
}
